import io from 'socket.io-client'
import { attachSocketTokenRefresh } from './socketAuth'

/**
 * One Socket.IO connection per tab, shared by every consumer.
 *
 * Each feature used to call io() itself. That does not multiplex: socket.io-client v2 only reuses
 * a cached Manager when the namespace is NOT already connected on it (`sameNamespace` in its
 * lookup()), and every consumer here connects to the default namespace — so each call built its
 * own Manager, its own WebSocket, and its own reconnect loop. A tab held as many connections,
 * handshakes and rejected-handshake log lines as it had features listening.
 *
 * Consumers acquire the socket with the listeners they need and release it on unmount; the
 * connection is opened on the first acquire and closed after the last release.
 */
let socket = null
let detachTokenRefresh = null
let consumers = 0

const createSocket = (url, token) =>
  io(url, {
    query: `token=${encodeURIComponent(token)}`,
    transports: ['websocket'],
    reconnection: true,
    reconnectionDelay: 1000, // Wait 1 second before reconnecting
    reconnectionDelayMax: 5000, // Maximum wait time between reconnections
    timeout: 20000, // Connection timeout
    // Deliberately no reconnectionAttempts cap. The old cap of 10 existed to bound the expired-
    // token retry storm, which attachSocketTokenRefresh now stops at its source; keeping it would
    // only leave notifications and incoming calls dead until a page reload after any outage
    // longer than ~40s.
  })

const teardown = () => {
  if (detachTokenRefresh) {
    detachTokenRefresh()
    detachTokenRefresh = null
  }

  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
}

/**
 * @param {Object} listeners map of event name -> handler, e.g. { incoming_call: onIncomingCall }
 * @returns {{ socket: object, release: Function }|null} null when there is nothing to connect
 *          with (no backend url, or no token because nobody is signed in)
 */
export const acquireCrmSocket = (listeners = {}) => {
  const url = process.env.REACT_APP_BACKEND_URL
  const token = sessionStorage.getItem('CRMToken')
  if (!url || !token) return null

  if (!socket) {
    socket = createSocket(url, token)
    // Keeps the handshake token fresh across reconnects, and stops reconnecting altogether once
    // there is no usable token left — otherwise every attempt writes a rejected-handshake line
    // in the backend log, forever.
    detachTokenRefresh = attachSocketTokenRefresh(socket)
  }

  const entries = Object.entries(listeners)
  entries.forEach(([event, handler]) => socket.on(event, handler))
  consumers += 1

  const acquired = socket
  let released = false

  return {
    socket: acquired,
    release: () => {
      // Guard against a double release: it would drop the count below the number of live
      // consumers and close a connection someone else is still using.
      if (released) return
      released = true

      // Remove only this consumer's listeners — the socket outlives an individual consumer.
      entries.forEach(([event, handler]) => acquired.off(event, handler))

      consumers = Math.max(0, consumers - 1)
      if (consumers === 0 && acquired === socket) teardown()
    },
  }
}
